import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { deleteAccount as deleteAccountRequest } from '../../utils/deleteAccount';
import { message } from '../Message';
import AppAccountLayout from '../AppAccountLayout';
import Spinner from '../Spinner';

const cardStyle = {
  background: '#1a1a1a',
  border: '1px solid #333',
  borderRadius: '12px',
  padding: '24px',
};

const dangerCardStyle = {
  ...cardStyle,
  borderColor: '#7f1d1d',
  background: '#1f1212',
};

const SettingsPage = ({ firebase, userData: initialUserData, setUser, onRemoveUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(initialUserData);
  const [isLoading, setIsLoading] = useState(!initialUserData);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!firebase?.auth?.currentUser) {
      return undefined;
    }

    firebase
      .getUser(firebase.auth.currentUser.uid)
      .then((doc) => {
        if (doc.exists()) {
          const data = { uid: doc.id, ...doc.data() };
          setUserData(data);
          setUser(data);
        }
      })
      .finally(() => setIsLoading(false));
  }, [firebase, setUser]);

  const confirmationPhrase = 'DELETE';
  const canDelete = confirmText.trim().toUpperCase() === confirmationPhrase;

  const handleDeleteAccount = async () => {
    if (!canDelete || isDeleting) {
      return;
    }

    const confirmed = window.confirm(t('settingsPage.deleteConfirm'));
    if (!confirmed) {
      return;
    }

    setIsDeleting(true);
    try {
      const idToken = await firebase.getIdToken();
      await deleteAccountRequest(idToken);
      await firebase.signOut();
      if (onRemoveUser) {
        onRemoveUser();
      }
      message.success(t('settingsPage.deleteSuccess'));
      navigate('/');
    } catch (error) {
      message.error(error.message || t('settingsPage.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading || !userData) {
    return <Spinner />;
  }

  return (
    <AppAccountLayout
      title={t('settingsPage.title')}
      subtitle={t('settingsPage.subtitle')}
      backLabel={t('settingsPage.backToApp')}
      userData={userData}
      t={t}
    >
      <div style={{ display: 'grid', gap: '24px' }}>
        <section style={cardStyle}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '16px' }}>
            {t('settingsPage.accountTitle')}
          </h2>
          <div style={{ display: 'grid', gap: '12px' }}>
            <div>
              <div style={{ color: '#888', fontSize: '0.875rem' }}>{t('settingsPage.email')}</div>
              <div>{userData.email}</div>
            </div>
            {(userData.firstName || userData.lastName) ? (
              <div>
                <div style={{ color: '#888', fontSize: '0.875rem' }}>{t('settingsPage.name')}</div>
                <div>{`${userData.firstName ?? ''} ${userData.lastName ?? ''}`.trim()}</div>
              </div>
            ) : null}
            <div>
              <div style={{ color: '#888', fontSize: '0.875rem' }}>{t('settingsPage.memberSince')}</div>
              <div>
                {userData.joinedDate
                  ? new Date(userData.joinedDate).toLocaleDateString()
                  : t('settingsPage.notAvailable')}
              </div>
            </div>
          </div>
        </section>

        <section style={dangerCardStyle}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '8px', color: '#fca5a5' }}>
            {t('settingsPage.deleteTitle')}
          </h2>
          <p style={{ color: '#ccc', marginBottom: '16px' }}>{t('settingsPage.deleteBody')}</p>
          <ul style={{ color: '#aaa', paddingLeft: '20px', marginBottom: '20px' }}>
            <li>{t('settingsPage.deleteItem1')}</li>
            <li>{t('settingsPage.deleteItem2')}</li>
            <li>{t('settingsPage.deleteItem3')}</li>
            <li>{t('settingsPage.deleteItem4')}</li>
          </ul>
          <label htmlFor="delete-confirm" style={{ display: 'block', marginBottom: '8px', color: '#ddd' }}>
            {t('settingsPage.deletePrompt', { phrase: confirmationPhrase })}
          </label>
          <input
            id="delete-confirm"
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={confirmationPhrase}
            style={{
              width: '100%',
              maxWidth: '320px',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid #555',
              background: '#111',
              color: '#fff',
              marginBottom: '16px',
            }}
          />
          <button
            type="button"
            className="btn btn-danger btn-sm rounded-pill"
            onClick={handleDeleteAccount}
            disabled={!canDelete || isDeleting}
          >
            {isDeleting ? t('settingsPage.deleting') : t('settingsPage.deleteButton')}
          </button>
        </section>
      </div>
    </AppAccountLayout>
  );
};

export default SettingsPage;
